import React, { useState, useEffect } from 'react';
import DnaExplorer from './DnaExplorer';

const D = {
  bg:'#060912', panel:'#0e1626', panel2:'#13203a', line:'#22324f',
  ink:'#eaf1ff', muted:'#8ea3c9', dim:'#5d6f93', accent:'#4cc9ff', accent2:'#ff3fb4',
  A:'#3ff2a6', T:'#ff5d7a', G:'#ffcf3f', C:'#4cc9ff',
};
const FD = "'Space Grotesk', sans-serif";
const FB = "'DM Sans', sans-serif";
const FM = "'IBM Plex Mono', monospace";

const SCREENS = [
  {
    eyebrow: 'Biology · DNA',
    title: 'You are written in a language of four letters',
    body: 'Coiled inside almost every one of your cells is about two metres of a single molecule: DNA. Along it runs a three-billion-letter instruction manual, written in an alphabet of just four bases — A, T, G and C. Their order is what makes you you. In this lesson you will learn how that code is built, then explore real DNA yourself: zooming in, spinning it, reading a gene, and mutating it.',
    stats: [
      { n: '~2 m', label: 'of DNA coiled inside one cell' },
      { n: '3.1 billion', label: 'letters in your genome' },
      { n: 'A T G C', label: 'four bases spell every living thing' },
    ],
    lumi: 'Four letters. The order they fall in is the difference between you, an oak tree, and a blue whale.',
  },
  {
    eyebrow: 'The rule',
    title: 'Two strands, joined by one strict pairing',
    body: 'DNA is a twisted ladder — the double helix. Two strands run in opposite directions, and the rungs between them are pairs of bases. The pairing is never random: A always joins T, and G always joins C. Because the rule never breaks, one strand always tells you the other, and that is exactly how DNA copies itself perfectly when a cell divides.',
    pairs: [
      { a: 'A', b: 'T', cA: D.A, cB: D.T, sym: '=', note: '2 hydrogen bonds' },
      { a: 'G', b: 'C', cA: D.G, cB: D.C, sym: '≡', note: '3 hydrogen bonds' },
    ],
    lumi: 'A holds T with two bonds, G holds C with three. Hold that rule — you will use it in every part of the explorer.',
  },
  {
    eyebrow: 'Your turn',
    title: 'Explore real DNA, end to end',
    body: 'Work through the explorer\'s tabs. Zoom from your whole body down to the molecule. Spin the double helix and click a rung. Read a gene as it is transcribed and translated into a protein, then hit Mutate and watch what one changed letter can do.',
    explorer: true,
    lumi: 'There are five tabs along the top. Take your time in each — the Mutate button in The Code is the one students love most.',
  },
  {
    eyebrow: 'Check',
    title: 'You changed one letter and the protein got shorter. Why?',
    body: 'In the explorer\'s Code tab, a mutation sometimes cuts the protein short. Using what you know about codons being read in triplets, what most likely happened?',
    options: [
      { text: 'The mutation created an early stop codon, so translation halted before the protein was finished.', correct: true, feedback: 'Correct. A base change can turn an amino-acid codon into one of the stop codons (like UAA). The ribosome stops there, so the protein is cut short — a nonsense mutation.' },
      { text: 'The DNA strand physically got shorter when the letter changed.', correct: false, feedback: 'The strand keeps the same length. What changed is the meaning: a codon became a stop signal, ending translation early.' },
      { text: 'Mutations always delete the second half of any gene.', correct: false, feedback: 'Not always. Many mutations are silent or change just one amino acid. This one happened to create a stop codon, ending the chain early.' },
      { text: 'The cell ran out of amino acids partway through.', correct: false, feedback: 'Amino acids were not the problem. A changed letter formed a stop codon, and the ribosome stops reading there.' },
    ],
    lumi: 'Think about what a stop codon does, and what it means to read the message three letters at a time.',
  },
  {
    eyebrow: 'Why it matters',
    title: 'From four letters to all of life, and to you',
    body: 'That four-letter code, read in triplets, builds the proteins that run every part of you, and it is copied into every new cell almost flawlessly. Change a single letter and you can shift a trait, or cause a disease. It is the thread that ties you to your parents, to your children, and to every living thing that has ever existed.',
    stats: [
      { n: 'Genes → proteins', label: "triplets of code build the body's machines" },
      { n: 'Replication', label: 'each new cell keeps one original strand' },
      { n: 'Heredity', label: 'why you carry traits from both parents' },
    ],
    lumi: 'Next time someone says it is in your DNA, you will know they mean it literally — written in A, T, G and C.',
  },
];

const DNA_POS_KEY = 'lumio_screen_pos_dna_lesson';

export default function DnaLesson({ onClose }) {
  const [idx, setIdx] = useState(() => {
    try {
      const pos = JSON.parse(localStorage.getItem(DNA_POS_KEY) || 'null');
      if (pos && Number.isInteger(pos.idx) && pos.idx < SCREENS.length && Date.now() - (pos.savedAt || 0) < 24 * 60 * 60 * 1000) {
        return pos.idx;
      }
    } catch (e) {}
    return 0;
  });
  const [pick, setPick] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(DNA_POS_KEY, JSON.stringify({ idx, savedAt: Date.now() })); } catch (e) {}
  }, [idx]);

  const sc = SCREENS[idx];
  const isCheck = !!sc.options;
  const correctPicked = isCheck && pick != null && sc.options[pick].correct;
  const gated = isCheck && !correctPicked;
  const isLast = idx === SCREENS.length - 1;
  const nextLabel = isLast ? 'Start over ↺' : gated ? 'Pick the best answer' : sc.explorer ? "I've explored it →" : 'Continue →';

  const goNext = () => {
    if (gated) return;
    if (isLast) { setIdx(0); setPick(null); return; }
    setIdx(i => i + 1);
    setPick(null);
  };
  const goBack = () => { if (idx > 0) { setIdx(i => i - 1); setPick(null); } else onClose(); };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: `radial-gradient(1100px 640px at 82% -10%,rgba(76,201,255,.12),transparent 60%),radial-gradient(900px 560px at 0% 100%,rgba(255,63,180,.1),transparent 55%),${D.bg}`, color: D.ink, fontFamily: FB, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 18px 80px' }}>
      <div style={{ width: '100%', maxWidth: 1140, display: 'flex', flexDirection: 'column' }}>

        {/* Header bar */}
        <div style={{ padding: '24px 4px 0', display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: D.muted, cursor: 'pointer', fontSize: 20, padding: '4px 8px', fontFamily: FB }}>×</button>
          <div style={{ flex: 1, display: 'flex', gap: 5 }}>
            {SCREENS.map((_, k) => (
              <div key={k} style={{ flex: 1, height: 5, borderRadius: 99, background: k <= idx ? D.accent : D.line, transition: 'background .45s ease' }} />
            ))}
          </div>
          <span style={{ fontFamily: FM, fontSize: 11, color: D.dim }}>{idx + 1}/{SCREENS.length}</span>
        </div>

        <div style={{ padding: '9px 4px 0', fontFamily: FM, fontSize: 12, color: '#6B77A0', letterSpacing: '0.04em' }}>🧬 Science · Year 10 · DNA</div>

        {/* Screen content */}
        <div style={{ flex: 1, padding: '22px 4px 0', display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontFamily: FM, fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: D.accent, fontWeight: 600 }}>{sc.eyebrow}</span>
          <h1 style={{ fontFamily: FD, fontWeight: 700, fontSize: 'clamp(26px,4vw,38px)', color: D.ink, letterSpacing: '-0.02em', lineHeight: 1.1, margin: '10px 0 13px' }}>{sc.title}</h1>
          <p style={{ fontSize: 16, color: D.muted, lineHeight: 1.62, maxWidth: 680, margin: 0, textWrap: 'pretty' }}>{sc.body}</p>

          {/* Stats feature */}
          {sc.stats && (
            <div style={{ marginTop: 22, display: 'flex', gap: 12, flexWrap: 'wrap', maxWidth: 760 }}>
              {sc.stats.map((st, i) => (
                <div key={i} style={{ flex: 1, minWidth: 150, background: D.panel, border: `1px solid ${D.line}`, borderRadius: 16, padding: 17 }}>
                  <div style={{ fontFamily: FD, fontWeight: 700, fontSize: 21, color: D.accent }}>{st.n}</div>
                  <div style={{ fontSize: 13, color: D.muted, marginTop: 5, lineHeight: 1.4 }}>{st.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Pairs feature */}
          {sc.pairs && (
            <div style={{ marginTop: 22, display: 'flex', gap: 16, flexWrap: 'wrap', maxWidth: 640 }}>
              {sc.pairs.map((p, i) => (
                <div key={i} style={{ flex: 1, minWidth: 230, background: D.panel, border: `1px solid ${D.line}`, borderRadius: 18, padding: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: p.cA, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FM, fontWeight: 700, fontSize: 22, color: '#04111f', boxShadow: `0 0 20px ${p.cA}`, flexShrink: 0 }}>{p.a}</div>
                  <div style={{ fontFamily: FM, fontSize: 20, color: D.muted }}>{p.sym}</div>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: p.cB, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FM, fontWeight: 700, fontSize: 22, color: '#04111f', boxShadow: `0 0 20px ${p.cB}`, flexShrink: 0 }}>{p.b}</div>
                  <div style={{ flex: 1, textAlign: 'right', fontSize: 12.5, color: D.muted, lineHeight: 1.4 }}>{p.note}</div>
                </div>
              ))}
            </div>
          )}

          {/* Explorer embed */}
          {sc.explorer && (
            <div style={{ marginTop: 18, border: `1px solid ${D.line}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.4)' }}>
              <DnaExplorer />
            </div>
          )}

          {/* Check options */}
          {sc.options && (
            <>
              <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
                {sc.options.map((o, oi) => {
                  const sel = pick === oi;
                  const border = sel ? (o.correct ? D.A : D.T) : D.line;
                  return (
                    <button key={oi} onClick={() => setPick(oi)} style={{ textAlign: 'left', background: D.panel, border: `1.5px solid ${border}`, borderRadius: 13, padding: '15px 17px', fontFamily: FB, fontWeight: 600, fontSize: 15, color: D.ink, cursor: 'pointer', transition: 'border-color .16s' }}>{o.text}</button>
                  );
                })}
              </div>
              {pick != null && (
                <div style={{ marginTop: 14, maxWidth: 640, background: sc.options[pick].correct ? 'rgba(63,242,166,.1)' : 'rgba(255,93,122,.1)', border: `1px solid ${sc.options[pick].correct ? 'rgba(63,242,166,.35)' : 'rgba(255,93,122,.35)'}`, borderRadius: 13, padding: '14px 16px', display: 'flex', gap: 11 }}>
                  <div style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%,#9af0e4,#0E9488)' }} />
                  <p style={{ fontSize: 13.5, color: '#cfe0f5', lineHeight: 1.55, margin: 0 }}>{sc.options[pick].feedback}</p>
                </div>
              )}
            </>
          )}

          <div style={{ flex: 1, minHeight: 18 }} />

          {/* Lumi panel */}
          {sc.lumi && (
            <div style={{ marginTop: 22, maxWidth: 760, background: 'rgba(76,201,255,.07)', border: '1px solid rgba(76,201,255,.22)', borderRadius: 15, borderTopLeftRadius: 5, padding: '14px 16px', display: 'flex', gap: 12 }}>
              <div style={{ width: 27, height: 27, flexShrink: 0, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%,#9af0e4,#0E9488)' }} />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontWeight: 700, fontSize: 12.5, color: D.ink }}>Lumi</span>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: D.accent, display: 'inline-block' }} />
                </div>
                <p style={{ fontSize: 13, color: D.muted, lineHeight: 1.5, margin: 0 }}>{sc.lumi}</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ padding: '18px 4px 0', display: 'flex', gap: 10, position: 'sticky', bottom: 0, background: `linear-gradient(transparent,${D.bg} 30%)` }}>
          <button onClick={goBack} style={{ background: D.panel2, color: D.muted, border: `1px solid ${D.line}`, borderRadius: 13, padding: '15px 20px', fontFamily: FB, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>←</button>
          <button onClick={goNext} style={{ flex: 1, background: gated ? '#16304a' : 'linear-gradient(135deg,#4cc9ff,#2f9fd6)', color: gated ? D.dim : '#04111f', border: 'none', borderRadius: 13, padding: 16, fontFamily: FB, fontWeight: 700, fontSize: 15, cursor: gated ? 'default' : 'pointer' }}>{nextLabel}</button>
        </div>

      </div>
    </div>
  );
}
